/**
 * API client — fetch wrapper con JWT, refresh automático y errores tipados.
 * Base URL: import.meta.env.VITE_API_BASE_URL (default /api/v1)
 */
import { useAuthStore } from './auth';
import type { ApiError } from '@/types';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
}

class ApiClient {
  private refreshing: Promise<boolean> | null = null;

  /**
   * Request principal. Auto-refresh del token si recibe 401.
   */
  async request<T = unknown>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { body, skipAuth, headers, ...rest } = options;

    const finalHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(headers as Record<string, string>),
    };

    if (!skipAuth) {
      const token = useAuthStore.getState().accessToken;
      if (token) finalHeaders.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${BASE_URL}${endpoint}`, {
      ...rest,
      headers: finalHeaders,
      body: body ? JSON.stringify(body) : undefined,
    });

    // 401 → intentar refresh una vez
    if (response.status === 401 && !skipAuth) {
      const refreshed = await this.attemptRefresh();
      if (refreshed) {
        return this.request<T>(endpoint, options);
      } else {
        useAuthStore.getState().logout();
        throw new Error('Sesión expirada');
      }
    }

    if (!response.ok) {
      const error = (await response.json().catch(() => ({}))) as ApiError;
      const message = Array.isArray(error.message)
        ? error.message.join(', ')
        : error.message || response.statusText;
      throw new Error(message);
    }

    // El backend envuelve respuestas en { success, data, ... } — extraer data
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const json = await response.json();
      return (json.data !== undefined ? json.data : json) as T;
    }
    return undefined as T;
  }

  /**
   * Intenta refrescar el access token una sola vez (evita loop).
   */
  private async attemptRefresh(): Promise<boolean> {
    if (this.refreshing) return this.refreshing;

    this.refreshing = (async () => {
      const refreshToken = useAuthStore.getState().refreshToken;
      if (!refreshToken) return false;

      try {
        const res = await fetch(`${BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (!res.ok) return false;
        const json = await res.json();
        const data = json.data || json;
        useAuthStore.getState().setTokens(data.accessToken, data.refreshToken);
        return true;
      } catch {
        return false;
      } finally {
        this.refreshing = null;
      }
    })();

    return this.refreshing;
  }

  // Helpers de método
  get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  patch<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body });
  }

  put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Upload multipart (archivos). NO pone Content-Type — fetch lo hace con boundary.
   */
  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const token = useAuthStore.getState().accessToken;
    const res = await fetch(`${BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (res.status === 401) {
      const refreshed = await this.attemptRefresh();
      if (refreshed) return this.upload<T>(endpoint, formData);
    }
    if (!res.ok) {
      const error = (await res.json().catch(() => ({}))) as ApiError;
      const message = Array.isArray(error.message)
        ? error.message.join(', ')
        : error.message || res.statusText;
      throw new Error(message);
    }
    const json = await res.json();
    return (json.data !== undefined ? json.data : json) as T;
  }
}

export const api = new ApiClient();
